import { stepper } from '@gitbook/doc-core';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { useBlockPaletteFactory } from '../common';

/**
 * Hook to use a commands palette definition to edit a hint block.
 */
export function useBlockStepperPaletteFactory(input: { nodeKey: string; stepperBlockKey: string }) {
    const { nodeKey, stepperBlockKey } = input;
    const editor = useEditor();
    const getIndex = () => {
        // If the node is the stepper block. Then we assume it is the first step.
        if (nodeKey === stepperBlockKey) {
            return 0;
        }
        const stepperBlock = editor.value.document.assertBlock(stepperBlockKey);
        return stepperBlock.getChildIndex(nodeKey);
    };
    return useBlockPaletteFactory({
        nodeKey,
        createExtraGroups: () => [
            {
                key: 'insert',
                commands: () => {
                    const index = getIndex();
                    return [
                        {
                            key: 'insert-step-before',
                            title: 'Insert a step before',
                            icon: Icon.SortUp,
                            trigger: () => {
                                editor.change((change) =>
                                    stepper.insertStepperStepAt(change, stepperBlockKey, index)
                                );
                            },
                        },
                        {
                            key: 'insert-step-after',
                            title: 'Insert a step after',
                            icon: Icon.SortDown,
                            trigger: () => {
                                editor.change((change) =>
                                    stepper.insertStepperStepAt(change, stepperBlockKey, index + 1)
                                );
                            },
                        },
                    ].filter(filterOutNullable);
                },
            },
            {
                key: 'move',
                commands: () => {
                    const index = getIndex();
                    const stepperBlock = editor.value.document.assertBlock(stepperBlockKey);
                    const stepperStep = stepperBlock.getNodeAtPath([index]);
                    assert(stepperStep, 'Stepper step must be defined');
                    return [
                        index > 0
                            ? {
                                  key: 'move-step-up',
                                  title: 'Move step up',
                                  icon: Icon.ArrowUp,
                                  trigger: () => {
                                      editor.change((change) =>
                                          stepper.moveStepperStep(change, stepperStep.key, 'up')
                                      );
                                  },
                              }
                            : null,
                        index < stepperBlock.nodes.size - 1
                            ? {
                                  key: 'move-step-down',
                                  title: 'Move step down',
                                  icon: Icon.ArrowDown,
                                  trigger: () => {
                                      editor.change((change) =>
                                          stepper.moveStepperStep(change, stepperStep.key, 'down')
                                      );
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable);
                },
            },
        ],
    });
}
